import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AdminJobConsoleService } from '../admin-job-console.service';
import { GlobalService } from '../../../../global/services/global.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { environment } from '../../../../../environments/environment';

interface JobConsoleParams {
  JOB_ID: string | number;
  JOB_TYPE: string;
  DOMAIN_TYPE: string;
  JOB_NAME: string;
  IS_ACTIVE: string;
  IS_ENABLE: string;
  SCHEDULE: string;
  PROC_CALL: string;
  DEFAULT_INPUT: string;
  DESCRIPTION: string;
  NOTIF_PROCESS_NAME: string;
  JOB_PARAMS: string;
  [key: string]: any;
}

@Component({
    selector: 'gtw-admin-job-console-edit',
    templateUrl: './admin-job-console-edit.component.html',
    styleUrls: ['./admin-job-console-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminJobConsoleEditComponent implements OnInit, OnDestroy {
  title = 'Job Console Edit';
  loading = false;
  minimise = false;
  crudLoading = false;
  isExecuting = false;

  jobConsoleParams: JobConsoleParams = {
    JOB_ID: '',
    JOB_TYPE: '',
    DOMAIN_TYPE: '',
    JOB_NAME: '',
    IS_ACTIVE: '',
    IS_ENABLE: '',
    SCHEDULE: '',
    PROC_CALL: '',
    DEFAULT_INPUT: '',
    DESCRIPTION: '',
    NOTIF_PROCESS_NAME: '',
    JOB_PARAMS: '',
  };

  readonly isActiveList: string[] = ['Y', 'N'];
  readonly isEnableList: string[] = ['Y', 'N'];
  readonly jobTypeList: string[] = ['STORED_PROCEDURE'];
  readonly domainTypeList: string[] = [
    'Foreign',
    'State',
    'CbCr',
    'Insurance',
    'E-File',
    'Domestic',
    'DST',
    'Partnerships',
    'FTC/FDII',
    'Others',
  ];

  private readonly baseUrl = environment.base_url;
  private readonly adminApiUrl = environment.admin_api_url;
  private readonly destroy$ = new Subject<void>();
  private originalParams: JobConsoleParams = { ...this.jobConsoleParams };

  constructor(
    @Optional() public dialogRef: MatDialogRef<AdminJobConsoleEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private jobConsoleService: AdminJobConsoleService,
    private globalService: GlobalService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const rowData = this.data?.rowData || {};
    this.jobConsoleParams = {
      ...this.jobConsoleParams,
      ...rowData,
    };
    this.jobConsoleParams.JOB_PARAMS =
      rowData.DEFAULT_INPUT ?? rowData.JOB_PARAMS ?? '';
    this.originalParams = JSON.parse(JSON.stringify(this.jobConsoleParams));
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  execute(): void {
    if (this.isExecuting) return;
    if (!this.jobConsoleParams?.JOB_ID) {
      this.snackbar.openSnackBar('Job ID is required to execute', 'gtw-snackbar--yellow');
      return;
    }

    this.isExecuting = true;
    this.cdr.detectChanges();

    const gp = this.globalService.global?.globalParams || {};
    const params = {
      job_id: this.jobConsoleParams.JOB_ID,
      job_params: this.jobConsoleParams.JOB_PARAMS ?? '',
      tax_year: gp.tax_year ?? '',
      scenario: gp.scenario ?? '',
      jcd_key: gp.jcd_key ?? '',
    };

    this.jobConsoleService
      .executeJob(this.adminApiUrl, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isExecuting = false;
          const success =
            res?.callSuccess === '1' || res?.data?.callSuccess === '1';
          if (success) {
            this.snackbar.openSnackBar(
              'Job Started Successfully, check the job log for the status.',
              'gtw-snackbar--green',
            );
          } else {
            const msg = res?.message || res?.data?.message || 'Unable to execute job';
            this.snackbar.openSnackBar(msg, 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.isExecuting = false;
          this.snackbar.openSnackBar('Error while executing job', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  update(): void {
    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    this.crudLoading = true;
    this.cdr.detectChanges();

    this.jobConsoleService
      .updateJob(this.baseUrl, this.jobConsoleParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Job Updated Successfully', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Update failed', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error while updating job', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  reset(): void {
    this.jobConsoleParams = JSON.parse(JSON.stringify(this.originalParams));
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.dialogRef?.close();
  }
}
